<template>
  <system-frame title="Varundus" hint="Varukoopia allalaadimine, taastamine üleslaaditud failist ja automaatne öine varundus Synologysse.">
    <v-row>
      <v-col cols="12" md="6" xl="4">
        <card class="h-100">
          <div class="text-h6 mb-2">Lae alla</div>
          <p class="text-grey-darken-1">
            Lae alla süsteemi varukoopia. Vali, mida varukoopia sisaldab.
          </p>
          <v-checkbox
            v-model="database"
            label="Andmebaas"
            :hint="'MySQL andmebaasi koopia (database.sql)' + (estimate ? ' · kuni ~' + size(estimate.database.bytes) : '')"
            persistent-hint
            class="mt-0"
          />
          <v-checkbox
            v-model="files"
            label="Failid"
            :hint="'Üleslaaditud pildid ja failid (uploads/)' + (estimate ? ' · ' + size(estimate.files.bytes) + ', ' + count(estimate.files.count) + ' faili' : '')"
            persistent-hint
          />
          <v-btn
            color="primary"
            class="mt-4"
            :disabled="!database && !files"
            :loading="downloading"
            @click="download"
          >
            <v-icon start>mdi-download</v-icon>
            Laadi alla
          </v-btn>
          <div v-if="progress" class="mt-3 text-body-2" :class="progress.state === 'failed' ? 'text-red-darken-2' : 'text-grey-darken-1'">
            <template v-if="progress.state === 'pending'">Alustan…</template>
            <template v-else-if="progress.state === 'running'">Koostan varukoopiat… {{ size(progress.written) }}</template>
            <template v-else-if="progress.state === 'done'">
              <v-icon size="small" color="green-darken-2">mdi-check</v-icon>
              Valmis: {{ size(progress.written) }}
            </template>
            <template v-else>Ebaõnnestus: {{ progress.error }}</template>
          </div>
        </card>
      </v-col>

      <v-col cols="12" md="6" xl="4">
        <card class="h-100">
          <div class="text-h6 mb-2">Taasta varukoopiast</div>

          <!-- A job is running or has finished: show it instead of the form. -->
          <template v-if="job || jobLost">
            <v-alert v-if="jobLost" type="warning" variant="tonal" density="compact" class="mb-3">
              {{ jobLost }}
              <div v-if="jobLost401" class="mt-2">
                <v-btn size="small" color="primary" :to="'/login?to=' + encodeURIComponent($route.fullPath)">Logi sisse</v-btn>
              </div>
            </v-alert>
            <template v-if="job">
              <div v-for="step in job.steps" :key="step.key" class="d-flex align-center py-1">
                <v-progress-circular v-if="step.state === 'running'" indeterminate size="18" width="2" color="primary" class="mr-3" />
                <v-icon v-else size="20" class="mr-2" :color="stepColor(step.state)">{{ stepIcon(step.state) }}</v-icon>
                <span :class="step.state === 'waiting' ? 'text-grey' : ''">{{ step.label }}</span>
              </div>
              <v-progress-linear
                v-if="job.state === 'running' && job.bytesTotal"
                :model-value="100 * job.bytesDone / job.bytesTotal"
                color="primary"
                height="6"
                rounded
                class="mt-2"
              />
              <div v-if="job.state === 'running' && job.bytesTotal" class="text-body-2 text-grey-darken-1 mt-1">
                {{ size(job.bytesDone) }} / {{ size(job.bytesTotal) }}
              </div>
              <v-alert v-if="job.state === 'done'" type="success" variant="tonal" density="compact" class="mt-3">
                Varukoopia on taastatud.
              </v-alert>
              <v-alert v-if="job.state === 'failed'" type="error" variant="tonal" density="compact" class="mt-3">
                Taastamine ebaõnnestus: {{ job.error }}
              </v-alert>
              <div v-if="job.safetyBackup" class="text-body-2 mt-3">
                Enne taastamist tehtud andmebaasi koopia:
                <a :href="safetyUrl(job.safetyBackup)">{{ job.safetyBackup }}</a>
              </div>
            </template>
            <v-btn v-if="!job || job.state !== 'running'" variant="text" class="mt-3" @click="resetRestore">
              Uus taastamine
            </v-btn>
          </template>

          <template v-else>
            <p class="text-grey-darken-1">
              Taasta sellelt lehelt alla laaditud varukoopia (zip) või automaatse varunduse krüpteeritud koopia (.zip.enc).
            </p>
            <v-file-input
              v-model="restoreFile"
              label="Varukoopia fail"
              accept=".zip,.enc,application/zip"
              prepend-icon="mdi-folder-zip-outline"
              density="compact"
              class="mt-2"
              :disabled="uploading"
              show-size
            />
            <v-checkbox
              v-model="restoreDatabase"
              label="Andmebaas"
              hint="Asendab kogu praeguse andmebaasi"
              persistent-hint
              class="mt-0"
              :disabled="uploading"
            />
            <v-checkbox
              v-model="restoreFiles"
              label="Failid"
              hint="Lisab ja kirjutab üle samanimelised failid, midagi ei kustuta"
              persistent-hint
              :disabled="uploading"
            />
            <v-alert type="warning" variant="tonal" density="compact" class="mt-4">
              <template v-if="restoreDatabase">
                Kõik praegused andmed asendatakse varukoopias olevatega — ka need, mis on lisatud pärast varukoopia tegemist.
                Enne taastamist tehakse praegusest andmebaasist turvakoopia.
                Taastamise järel võib olla vaja uuesti sisse logida.
              </template>
              <template v-else>
                Varukoopias olevad failid kirjutatakse samanimeliste praeguste failide peale.
              </template>
            </v-alert>
            <v-text-field
              v-model="confirmText"
              label="Kinnitamiseks kirjuta TAASTA"
              density="compact"
              class="mt-4"
              :disabled="uploading"
              autocomplete="off"
            />
            <v-btn
              color="red-darken-2"
              :disabled="!canRestore"
              :loading="uploading"
              @click="restore"
            >
              <v-icon start>mdi-backup-restore</v-icon>
              Taasta
            </v-btn>
            <template v-if="uploading && uploadTotal">
              <v-progress-linear :model-value="100 * uploadLoaded / uploadTotal" color="primary" height="6" rounded class="mt-3" />
              <div class="text-body-2 text-grey-darken-1 mt-1">
                Laen üles… {{ size(uploadLoaded) }} / {{ size(uploadTotal) }}
              </div>
            </template>
            <v-alert v-if="restoreError" type="error" variant="tonal" density="compact" class="mt-3">
              {{ restoreError }}
            </v-alert>
          </template>
        </card>
      </v-col>

      <auto-backup :estimate="estimate" />
    </v-row>
  </system-frame>
</template>

<script>
import SystemFrame from '@/components/SystemFrame.vue'
import api from '@/api'
import AutoBackup from './AutoBackup.vue'

// The running restore's id, kept across a login: the restored database brings
// its own sessions table, which can end this one mid-restore.
const JOB_KEY = 'intra.restoreJob'
const remember = id => { try { id ? sessionStorage.setItem(JOB_KEY, id) : sessionStorage.removeItem(JOB_KEY) } catch (e) { /* private window */ } }
const recall = () => { try { return sessionStorage.getItem(JOB_KEY) } catch (e) { return null } }

const randomToken = () => Array.from(crypto.getRandomValues(new Uint8Array(12)), b => b.toString(16).padStart(2, '0')).join('')

export default {
  components: { AutoBackup, SystemFrame },
  data () {
    return {
      database: true,
      files: false,
      estimate: null,
      downloading: false,
      progress: null,
      progressTimer: null,

      restoreFile: null,
      restoreDatabase: true,
      restoreFiles: false,
      confirmText: '',
      uploading: false,
      uploadLoaded: 0,
      uploadTotal: 0,
      restoreError: null,
      job: null,
      jobLost: null,
      jobLost401: false,
      jobTimer: null
    }
  },
  computed: {
    // Vuetify 3's file input gives a File or an array of them, by version.
    chosenFile () {
      return Array.isArray(this.restoreFile) ? this.restoreFile[0] : this.restoreFile
    },
    canRestore () {
      return !!this.chosenFile && (this.restoreDatabase || this.restoreFiles) && this.confirmText === 'TAASTA'
    }
  },
  mounted () {
    api.getBackupEstimate().then(estimate => { this.estimate = estimate }).catch(() => {})
    const id = recall()
    if (id) this.pollJob(id)
  },
  beforeUnmount () {
    // `gone` as well as the timers: a request still on its way when the page
    // is left would otherwise set its timer after these were cleared, and the
    // polling went on behind whatever page came next.
    this.gone = true
    clearTimeout(this.progressTimer)
    clearTimeout(this.jobTimer)
  },
  methods: {
    size (bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' kB'
      if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1).replace('.', ',') + ' MB'
      return (bytes / 1024 / 1024 / 1024).toFixed(2).replace('.', ',') + ' GB'
    },
    count (n) {
      return n.toLocaleString('et-EE')
    },
    download () {
      const params = { token: randomToken() }
      if (this.database) params.database = 1
      if (this.files) params.files = 1
      if (!this.database && !this.files) return

      // The backup streams as a file download; trigger it via a temporary anchor
      // so the session cookie is sent and the response is not buffered in memory.
      // How far it has got is asked of the server by the token.
      this.downloading = true
      this.progress = { state: 'pending', written: 0 }
      clearTimeout(this.progressTimer)
      const a = document.createElement('a')
      a.href = api.backupUrl(params)
      a.download = ''
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      this.pollProgress(params.token, Date.now())
    },
    pollProgress (token, started) {
      api.getBackupProgress(token).then(progress => {
        this.progress = progress
      }).catch(() => {}).finally(() => {
        if (this.gone) return
        const state = this.progress && this.progress.state
        // A request that never reached the server stays pending; stop asking.
        const gaveUp = state === 'pending' && Date.now() - started > 30 * 1000
        if (state === 'done' || state === 'failed' || gaveUp) {
          this.downloading = false
          if (gaveUp) this.progress = null
          return
        }
        this.progressTimer = setTimeout(() => this.pollProgress(token, started), 700)
      })
    },
    async restore () {
      if (!this.canRestore) return
      const fields = {
        database: this.restoreDatabase,
        files: this.restoreFiles,
        confirm: this.confirmText
      }

      this.uploading = true
      this.restoreError = null
      this.uploadLoaded = 0
      this.uploadTotal = this.chosenFile.size
      try {
        const job = await api.restoreBackup(this.chosenFile, fields, (loaded, total) => {
          this.uploadLoaded = loaded
          this.uploadTotal = total
        })
        this.job = job
        remember(job.id)
        this.pollJob(job.id)
      } catch (err) {
        this.restoreError = (err && err.message) || 'Taastamine ebaõnnestus'
      } finally {
        this.uploading = false
      }
    },
    pollJob (id) {
      clearTimeout(this.jobTimer)
      api.getRestoreJob(id).then(job => {
        if (this.gone) return
        this.job = job
        this.jobLost = null
        this.jobLost401 = false
        if (job.state !== 'running') {
          remember(null)
          return
        }
        this.jobTimer = setTimeout(() => this.pollJob(id), 1000)
      }).catch(err => {
        if (this.gone) return
        const status = err && err.status
        if (status === 404) {
          // The server restarted, or this is an old id: nothing to follow.
          remember(null)
          if (!this.job) return
          this.jobLost = 'Taastamise olekut ei ole enam serveris (server võis taaskäivituda).'
          return
        }
        // While the database is replaced the server can briefly not answer, and
        // the restored sessions table may not know this login.
        if (status === 401) {
          this.jobLost401 = true
          this.jobLost = 'Sisselogimine lõppes taastamise ajal. Taastamine jätkub serveris — logi uuesti sisse, et tulemust näha.'
          return
        }
        this.jobTimer = setTimeout(() => this.pollJob(id), 2000)
      })
    },
    resetRestore () {
      remember(null)
      this.job = null
      this.jobLost = null
      this.jobLost401 = false
      this.restoreFile = null
      this.confirmText = ''
      this.restoreError = null
    },
    safetyUrl (name) {
      return api.restoreSafetyUrl(name)
    },
    stepIcon (state) {
      return { done: 'mdi-check-circle', failed: 'mdi-close-circle' }[state] || 'mdi-circle-outline'
    },
    stepColor (state) {
      return { done: 'green-darken-2', failed: 'red-darken-2' }[state] || 'grey'
    }
  }
}
</script>
